import {
   ChangeDetectionStrategy,
   ChangeDetectorRef,
   Component,
   ElementRef,
   EventEmitter,
   HostListener,
   Input,
   OnDestroy,
   Output,
   SimpleChanges,
   ViewChild,
   ViewEncapsulation,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { Store } from '@ngrx/store';
import { Observable, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { accountsRoute } from 'src/app/app-routing.module';
import { CurrentUser } from 'src/app/authentication/models/current-user.interface';
import { GlobalSearchResultSet } from 'src/app/common-components/global-search/models/global-search.model';
import {
   globalSearchDeviceSearched,
   globalSearchSearchResultCleared,
   globalSearchSearchTextChanged,
} from 'src/app/common-components/global-search/state/global-search.actions';
import { searchDebounceTime } from 'src/app/common-components/utils/search-debounce-rxjs';
import { MenuItem } from '../site-menu/menu-item';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    selector: 'ndt-site-layout-view',
    styleUrls: ['./site-layout-view.component.scss'],
    templateUrl: './site-layout-view.component.html',
    standalone: false
})
export class SiteLayoutViewComponent implements OnDestroy {
   @ViewChild('searchContainer', { static: false }) searchContainer!: ElementRef;

   @ViewChild('searchButton', { read: ElementRef }) searchButton: ElementRef;

   @Input() currentUser: CurrentUser | null;

   @Input() isLocalUser: boolean | null;

   @Input() adminToken: boolean;

   @Input() searchText: string;

   @Input() selectedAccountName: string | null;

   @Input() isOnSystemPage: boolean | undefined;

   @Input() hideShell = false;

   @Input() menuExpanded;

   @Input() menuItems: MenuItem[];

   @Input() mobileView: boolean;

   @Input() showCallQueues: boolean | null;

   @Input() showTopBar: boolean;

   @Input() tabletView: boolean;

   @Input() showExtensionRegisterPopup: boolean;

   @Output() callQueueManagerClicked = new EventEmitter<void>();

   @Output() changePasswordClicked = new EventEmitter<void>();

   @Output() submitFeedbackClicked = new EventEmitter<void>();

   @Output() searchTextSubmitted = new EventEmitter<string>();

   @Output() newTicketClicked = new EventEmitter<void>();

   @Output() searchTextChange = new EventEmitter<string>();

   @Output() signOutClicked = new EventEmitter<void>();

   @Output() toggleMenuClicked = new EventEmitter<boolean>();

   @Output() userPreferencesClicked = new EventEmitter<void>();

   @Output() customerViewClicked = new EventEmitter<boolean>();

   @Output() changeAccountClicked = new EventEmitter<void>();

   @Output() errorEmit = new EventEmitter<null>();

   @Output() itemSelected = new EventEmitter();

   @Output() toggleShowExtensionRegisterPopup = new EventEmitter<boolean>();

   debounce$: Subscription;

   public searchResults: GlobalSearchResultSet | null = null;

   public showDropdown = false;

   searchTextFormControl: UntypedFormControl;

   form: UntypedFormGroup;

   private shortcircuitSearch = false;

   hasErrors = false;

   accountsRoute = accountsRoute;

   destroyed$ = new Subject<void>();

   searchDebounceTime = 400;

   get formControls() {
      return this.form.controls;
   }

   constructor(formBuilder: UntypedFormBuilder, private store: Store, private cdRef: ChangeDetectorRef) {
      this.searchTextFormControl = formBuilder.control('', { validators: Validators.minLength(3) });
      this.form = formBuilder.group({ searchText: this.searchTextFormControl });
   }

   @HostListener('document:click', ['$event'])
   handleClickOutsideSearchInput(event: Event): void {
      const targetElement = event?.target as HTMLElement;
      if (!this.searchContainer.nativeElement.contains(targetElement)) {
         this.showDropdown = false;
         this.cdRef.markForCheck();
      } else if (
         (this.searchContainer.nativeElement.contains(targetElement) && this.showDropdown == false) ||
         this.searchButton.nativeElement.contains(targetElement)
      ) {
         const searchText = this.formControls.searchText.value;
         if (searchText) {
            this.store.dispatch(globalSearchSearchTextChanged({ searchText }));
         }
         this.performPreSearch();
         this.cdRef.markForCheck();
      }
   }

   handleClickInside(event: Event): void {
      event.stopPropagation();
   }

   ngOnChanges(changes: SimpleChanges): void {
      if (changes.searchText && changes.searchText.currentValue !== changes.searchText.previousValue) {
         this.searchTextFormControl.setValue(changes.searchText.currentValue);
      }
   }

   ngOnInit(): void {
      this.searchTextFormControl.reset();
      this.form.controls.searchText.valueChanges
         .pipe(debounceTime(this.searchDebounceTime), distinctUntilChanged(), takeUntil(this.destroyed$))
         .subscribe((value) => {
            this.performPreSearch();
            this.cdRef.markForCheck();
         });
   }

   onSubmit(): void {
      this.performPreSearch();
      this.cdRef.markForCheck();
   }

   ngOnDestroy(): void {
      this.destroyed$.next();
   }

   searchDebounce<T>(time: number = searchDebounceTime): (source: Observable<T>) => Observable<T> {
      return debounceTime(time);
   }

   onCallQueueManagerClick(): void {
      this.callQueueManagerClicked.emit();
   }

   onToggleMenuClicked(): void {
      this.toggleMenuClicked.emit(!this.menuExpanded);
   }

   onSearchIconClick(): void {
      this.performPreSearch();
      this.showDropdown = true;
   }

   onSignOutClick(): void {
      this.signOutClicked.emit();
   }

   onCreateClick(): void {
      this.newTicketClicked.emit();
   }

   onToggleShowExtensionRegisterPopup(isPopupShown: boolean): void {
      this.toggleShowExtensionRegisterPopup.emit(isPopupShown);
   }

   onUserPreferencesClick(): void {
      this.userPreferencesClicked.emit();
   }

   onCustomerViewClick(slideToggleChange: MatSlideToggleChange): void {
      this.customerViewClicked.emit(slideToggleChange.checked);
   }

   onChangePasswordClick(): void {
      this.changePasswordClicked.emit();
   }

   isValidMacAddress(searchText: string): boolean {
      const macAddressRegex = /^([0-9A-Fa-f]{2}[:.-]?){5}([0-9A-Fa-f]{2})$/;
      return macAddressRegex.test(searchText);
   }

   handleItemSelectCompleted(): void {
      this.searchTextFormControl.reset();
      this.clearSearchResults();
   }

   private search(isDebounceSource = false): void {
      if (!(this.shortcircuitSearch && isDebounceSource)) {
         const searchText = this.searchText;
         if (this.hasErrors || !searchText) {
            this.errorEmit.emit();
         } else {
            if (!this.formControls.searchText.hasError('minLength')) {
               this.store.dispatch(globalSearchSearchTextChanged({ searchText }));
            }
         }
      }

      this.shortcircuitSearch = false;
   }

   private macAddressSearch(isDebounceSource = false): void {
      if (!(this.shortcircuitSearch && isDebounceSource)) {
         const searchText = this.searchText;
         if (this.hasErrors || !this.formControls.searchText.value) {
            this.errorEmit.emit();
         } else {
            if (!this.formControls.searchText.hasError('minLength')) {
               this.store.dispatch(globalSearchDeviceSearched({ searchText }));
            }
         }
      }

      this.shortcircuitSearch = false;
   }

   private performPreSearch(): void {
      this.shortcircuitSearch = true;
      this.searchText = this.formControls.searchText.value;
      if (this.searchText !== null) {
         this.searchText = this.searchText.replace(/[-():\\]/g, '').trim();
      }

      const isValidMac = this.isValidMacAddress(this.searchText);

      if (this.searchText === '') {
         this.clearSearchResults();
         return;
      }

      if (this.searchText && this.searchText.length >= 3 && !this.formControls.searchText.hasError('minlength')) {
         this.clearSearchResults();
         this.showDropdown = true;
         if (isValidMac) {
            this.macAddressSearch();
         } else {
            this.search();
         }
      } else {
         this.clearSearchResults();
      }
   }

   private clearSearchResults(): void {
      this.showDropdown = false;
      this.store.dispatch(globalSearchSearchResultCleared());
   }
}
export function truncateAccountName(name, maxLength): string {
   //Case if String does not contain spaces meaning 1 Line
   if (!/\s/.test(name)) return name.substring(0, 20) + '...';

   if (name.length > maxLength) {
      return name.substring(0, maxLength - 3) + '...';
   }
   return name;
}
